import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  PathologyDeliverySchema,
  PathologyListResponseSchema,
  type PathologyDelivery,
  type PathologyListQuery,
  type PathologyListResponse,
  type SignPathologyDeliveryInput,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

export const pathologyKeys = {
  all: ['pathology'] as const,
  list: (q: PathologyListQuery) => ['pathology', 'list', q] as const,
  detail: (id: string) => ['pathology', 'detail', id] as const,
};

function buildQs(q: PathologyListQuery): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') usp.set(k, String(v));
  }
  return usp.toString();
}

export function usePathologyList(
  q: PathologyListQuery,
): UseQueryResult<PathologyListResponse, Error> {
  return useQuery({
    queryKey: pathologyKeys.list(q),
    queryFn: () =>
      apiFetch<PathologyListResponse>(`/api/pathology?${buildQs(q)}`, {
        parseAs: PathologyListResponseSchema,
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useSignPathologyDelivery(): UseMutationResult<
  PathologyDelivery,
  Error,
  SignPathologyDeliveryInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignPathologyDeliveryInput) =>
      apiFetch<PathologyDelivery>('/api/pathology/sign', {
        method: 'POST',
        body: input,
        parseAs: PathologyDeliverySchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pathologyKeys.all });
    },
  });
}
